import { useMemo } from 'react';

import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { Checkbox } from '@/components/ui/checkbox';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useDialogs from '@/hooks/useDialogs';
import { selectHasOtherAutoOpenWebview } from '@/redux/dialogs/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import { RootState } from '@/shared/types/state';
import { capitalizeFirstLetter } from '@/util/util';

interface EventProps<FormType extends FieldValues> {
  updateRedux: () => void;
  index?: number;
  cardIndex?: number;
  actionId?: string;
  control: Control<FormType>;
}

const CarouselWebview = <FormType extends FieldValues>({
  updateRedux,
  index,
  cardIndex,
  actionId,
  control,
}: EventProps<FormType>) => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const hasOtherAutoOpen = useSelector((state: RootState) =>
    selectHasOtherAutoOpenWebview(state, actionId, cardIndex, index)
  );

  const { eventsOptions } = useDialogs(brainId);
  const heightOptions = useMemo(
    () => [
      { value: 'tall', label: t('dialog.webview.tall') },
      { value: 'compact', label: t('dialog.webview.compact') },
      { value: 'full', label: t('dialog.webview.full') },
      { value: 'new_window', label: t('dialog.webview.new_window') },
    ],
    [t]
  );

  return (
    <>
      <Controller
        control={control}
        name={'url' as Path<FormType>}
        render={({ field, fieldState: { error } }) => (
          <>
            <Input
              {...field}
              value={field.value ?? ''}
              onChange={(event) => {
                field.onChange(event);
                updateRedux();
              }}
              placeholder={t('dialog.carousel.url_placeholder')}
              aria-invalid={!!error}
            />
            {error && (
              <p className="text-sm text-warning">{capitalizeFirstLetter(error.message)}</p>
            )}
          </>
        )}
      />
      <div className="flex flex-col gap-4">
        <div>
          <Label className="mb-2 block">{t('dialog.trigger_node_placeholder')}</Label>
          <FormField
            control={control}
            name={'trigger_node_id' as Path<FormType>}
            render={({ field, fieldState: { error } }) => (
              <Select
                value={(field.value as string) || undefined}
                onValueChange={(value) => {
                  field.onChange(value);
                  updateRedux();
                }}
              >
                <SelectTrigger
                  id={`trigger_node_id-carousel-${index ? index : ''}-${cardIndex}`}
                  className={error ? 'border-destructive' : ''}
                >
                  <SelectValue placeholder={t('dialog.select_trigger_node')} />
                </SelectTrigger>
                <SelectContent>
                  {eventsOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div>
          <Label className="mb-2 block">{t('dialog.height_placeholder')}</Label>
          <FormField
            control={control}
            name={'height' as Path<FormType>}
            render={({ field, fieldState: { error } }) => (
              <Select
                value={(field.value as string) || undefined}
                onValueChange={(value) => {
                  field.onChange(value);
                  updateRedux();
                }}
              >
                <SelectTrigger
                  id={`height-carousel-${index ? index : ''}-${cardIndex}`}
                  className={error ? 'border-destructive' : ''}
                >
                  <SelectValue placeholder={t('common.select_an_option')} />
                </SelectTrigger>
                <SelectContent>
                  {heightOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <FormField
          control={control}
          name={'auto_open' as Path<FormType>}
          render={({ field }) => {
            const checkboxId = `auto_open-carousel-${index ? index : ''}-${cardIndex}`;
            return (
              <div className="flex items-start gap-2 align-middle">
                <Checkbox
                  id={checkboxId}
                  checked={!!field.value}
                  onCheckedChange={(checked) => {
                    if (checked && hasOtherAutoOpen) {
                      toast.warning(t('dialog.webview.auto_open_warning'));
                    }
                    field.onChange(checked);
                    updateRedux();
                  }}
                  className="mt-0.5"
                />
                <div className="flex flex-col gap-1">
                  <Label htmlFor={checkboxId} className="cursor-pointer text-sm font-medium">
                    {t('dialog.webview.auto_open_label')}
                  </Label>
                  <p className="text-sm text-foreground-muted">
                    {t('dialog.webview.auto_open_description')}
                  </p>
                </div>
              </div>
            );
          }}
        />
      </div>
    </>
  );
};

export default CarouselWebview;
